import { seasonYear, isApplicationOpen } from "../../utils/seasonDates";
import { SeasonBar } from "../../components/Seasons/SeasonBar";
import { useSeason } from "../../context/SeasonContext";
import { useEffect, useState } from "react";
import { SeasonLink as Link } from "../../components/Seasons/SeasonLink";
import { useAuth } from "../../context/AuthContext";
import { getPublicShowcaseCases } from "../../api/caseShowcase";
import {
  getScheduleEntries,
  getScheduleEntriesNoAuth,
} from "../../api/schedule";
import styles from "./landingPage.module.css";
import team from "../../assets/icons/imageForLand.png";
import { LandingNews } from "../News/LandingNews";

const MONTHS = [
  "января",
  "февраля",
  "марта",
  "апреля",
  "мая",
  "июня",
  "июля",
  "августа",
  "сентября",
  "октября",
  "ноября",
  "декабря",
];

const NAV_ITEMS = [
  { label: "Новости", href: "#news" },
  { label: "Расписание", href: "#schedule-preview" },
  { label: "Треки", href: "#tracks" },
  { label: "Кейсы", href: "#cases" },
  { label: "Эксперты", href: "#experts" },
];

const TRACKS_TOP = [
  {
    title: "ЮФУ.Про Партнеров",
    description: "Реальные бизнес-кейсы от компаний и партнеров региона.",
    note: "01 / INDUSTRY FOCUS",
    tone: "light",
  },
  {
    title: "ЮФУ.Про Стартапы",
    description:
      "Запуск инновационных продуктов и тестирование бизнес-моделей.",
    note: "02 / HIGH-GROWTH",
    tone: "primary",
  },
  {
    title: "ЮФУ.Про Исследования",
    description:
      "Фундаментальные и прикладные разработки на стыке науки и практики.",
    note: "03 / SCIENTIFIC RIGOR",
    tone: "muted",
  },
];

// const TRACKS_BOTTOM = [
//   {
//     title: 'ЮФУ.Про Общество',
//     description: 'Социальные инициативы, меняющие жизнь университета и города.',
//   },
//   {
//     title: 'ЮФУ.Про Организацию',
//     description: 'Проекты по развитию процессов, событий и инфраструктуры ЮФУ.',
//   },
// ];

const BENEFITS = [
  "Работа в междисциплинарной команде единомышленников.",
  "Прямой контакт с экспертами, наставниками и работодателями.",
  "Портфолио реального проекта с внедряемым результатом.",
  "ЮФУнты, которые можно обменивать на мерч, события и бонусы.",
];

const SOCIALS = [
  { label: "ВКонтакте", href: "https://vk.com/sfedupro" },
  { label: "Telegram", href: "https://t.me/sfedupro" },
  { label: "SfeduNet", href: "https://startup.sfedu.ru/SfeduNet/" },
];

function parseEntryDate(rawLabel, yearHint) {
  const label = String(rawLabel || "")
    .trim()
    .toLowerCase();
  if (!label) return null;

  const dottedMatch = label.match(/(\d{1,2})\.(\d{1,2})(?:\.(\d{2,4}))?/);
  if (dottedMatch) {
    const day = Number(dottedMatch[1]);
    const month = Number(dottedMatch[2]);
    const year = Number(dottedMatch[3] || yearHint);
    const normalizedYear = year < 100 ? 2000 + year : year;
    const parsed = new Date(normalizedYear, month - 1, day);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }

  const monthPattern = new RegExp(
    `(\\d{1,2})\\s+(${MONTHS.join("|")})(?:\\s+(\\d{4}))?`,
    "i",
  );
  const namedMatch = label.match(monthPattern);
  if (namedMatch) {
    const day = Number(namedMatch[1]);
    const month =
      MONTHS.findIndex(
        (monthName) => monthName === namedMatch[2].toLowerCase(),
      ) + 1;
    const year = Number(namedMatch[3] || yearHint);
    const parsed = new Date(year, month - 1, day);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }

  return null;
}

export function LandingPage() {
  const { isAuthenticated } = useAuth();
  const { season, withSeason } = useSeason();
  const yearHint = seasonYear(season);
  const recruiting = isApplicationOpen(season);
  const applicationHref = season?.application_url || withSeason("/my-season");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showcasePreview, setShowcasePreview] = useState([]);
  const [schedulePreview, setSchedulePreview] = useState([]);
  const [activeSection, setActiveSection] = useState("");

  useEffect(() => {
    getPublicShowcaseCases({
      limit: 3,
      offset: 0,
      sort_by: "source_created_at",
      sort_dir: "desc",
    })
      .then((response) => setShowcasePreview(response?.items || []))
      .catch(() => setShowcasePreview([]));
  }, []);

  useEffect(() => {
    const fetchSchedule = isAuthenticated
      ? getScheduleEntries
      : getScheduleEntriesNoAuth;

    fetchSchedule()
      .then((response) => {
        const items = Array.isArray(response?.items) ? response.items : [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const prepared = items.map((item, index) => ({
          item,
          index,
          parsedDate: parseEntryDate(item.date_label, yearHint),
        }));

        const upcoming = prepared
          .filter((entry) => entry.parsedDate && entry.parsedDate >= today)
          .sort((a, b) => a.parsedDate.getTime() - b.parsedDate.getTime())
          .map((entry) => entry.item);

        const undated = prepared
          .filter((entry) => !entry.parsedDate)
          .sort((a, b) => a.index - b.index)
          .map((entry) => entry.item);

        const past = prepared
          .filter((entry) => entry.parsedDate && entry.parsedDate < today)
          .sort((a, b) => b.parsedDate.getTime() - a.parsedDate.getTime())
          .map((entry) => entry.item);

        const ordered = [...upcoming];
        if (ordered.length < 2) {
          ordered.push(...undated);
        }
        if (ordered.length < 2) {
          ordered.push(...past);
        }

        setSchedulePreview(ordered.slice(0, 2));
      })
      .catch(() => setSchedulePreview([]));
  }, [isAuthenticated, yearHint]);

  useEffect(() => {
    const sections = NAV_ITEMS.map((item) => item.href.replace("#", ""))
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    if (!sections.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target?.id) {
          setActiveSection(visible.target.id);
        }
      },
      {
        rootMargin: "-35% 0px -50% 0px",
        threshold: [0.15, 0.35, 0.6],
      },
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  const handleNavClick = (item, e) => {
    e.preventDefault();
    const targetId = item.href.replace("#", "");
    const target = document.getElementById(targetId);
    if (target) {
      target.scrollIntoView({ behavior: "smooth" });
      setActiveSection(targetId);
    }
    setIsMenuOpen(false);
  };

  return (
    <div className={styles.pageShell}>
      <header className={styles.topNav}>
        <div className={styles.topNavInner}>
          <div className={styles.brand}>ЮФУ.Про</div>

          <nav className={styles.menu}>
            {NAV_ITEMS.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className={
                  item.href === `#${activeSection}`
                    ? styles.menuActive
                    : styles.menuLink
                }
                onClick={(e) => handleNavClick(item, e)}
              >
                {item.label}
              </a>
            ))}
          </nav>

          <SeasonBar compact />

          <div className={styles.desktopActions}>
            {isAuthenticated ? (
              <Link to="/profile" className={styles.btnGhost}>
                Кабинет
              </Link>
            ) : (
              <Link to="/login" className={styles.btnGhost}>
                Вход
              </Link>
            )}
          </div>

          <button
            className={styles.burgerButton}
            type="button"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Закрыть меню" : "Открыть меню"}
            aria-expanded={isMenuOpen}
            aria-controls="landing-mobile-menu"
          >
            <span className={styles.burgerLine} />
            <span className={styles.burgerLine} />
            <span className={styles.burgerLine} />
          </button>
        </div>

        {isMenuOpen && (
          <div className={styles.mobileMenu} id="landing-mobile-menu">
            <nav className={styles.mobileMenuNav}>
              {NAV_ITEMS.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  className={
                    item.href === `#${activeSection}`
                      ? styles.mobileMenuActive
                      : styles.mobileMenuLink
                  }
                  onClick={(e) => handleNavClick(item, e)}
                >
                  {item.label}
                </a>
              ))}
            </nav>
            <div className={styles.mobileActions}>
              {isAuthenticated ? (
                <Link
                  to="/profile"
                  className={styles.btnPrimary}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Кабинет
                </Link>
              ) : (
                <Link
                  to="/login"
                  className={styles.btnGhost}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Вход
                </Link>
              )}
            </div>
          </div>
        )}
      </header>

      <main className={styles.main}>
        <section className={styles.heroSection}>
          <div className={styles.heroGrid}>
            <div className={styles.heroContent}>
              <span className={styles.badge}>
                {season?.title || "Академический катализатор"}
              </span>
              <h1 className={styles.heroTitle}>
                Экосистема
                <br />
                <span>Проектов</span>
              </h1>
              <p className={styles.heroText}>
                ЮФУ.Про объединяет студентов в междисциплинарные команды для
                работы над задачами со звёздочкой. Формат длится 2,5 месяца и
                ведет к результатам, которые внедряются у партнеров и в
                университете.
              </p>
              <div className={styles.heroButtons}>
                {recruiting && (
                  <a
                    className={styles.btnPrimaryLarge}
                    href={applicationHref}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Подать заявку
                  </a>
                )}
                <Link className={styles.btnOutlineLarge} to="/cases">
                  Биржа кейсов
                </Link>
                <Link className={styles.btnOutlineLarge} to="/schedule">
                  Расписание
                </Link>
                {recruiting && season?.expert_application_url && (
                  <a
                    className={styles.btnOutlineLarge}
                    href={season.expert_application_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Стать экспертом
                  </a>
                )}
              </div>
            </div>

            <div className={styles.heroVisual} aria-hidden="true">
              <div className={styles.blobOne} />
              <div className={styles.blobTwo} />
              <div className={styles.shapeA} />
              <div className={styles.shapeB} />
              <div className={styles.shapeCard}>
                <div className={styles.shapeIcon} />
                <div>
                  <p>Междисциплинарность</p>
                  <span>Командный интеллект</span>
                </div>
                <div className={styles.shapeLines}>
                  <i />
                  <i />
                </div>
              </div>
            </div>
          </div>
        </section>

        <LandingNews />

        <section
          className={styles.schedulePreviewSection}
          id="schedule-preview"
        >
          <div className={styles.sectionHeadBetween}>
            <h2>
              Расписание <span>интенсива</span>
            </h2>
            <Link
              to={isAuthenticated ? "/schedule" : "/login"}
              className={styles.btnOutlineLarge}
            >
              Полное расписание
            </Link>
          </div>

          <div className={styles.schedulePreviewGrid}>
            {schedulePreview.length ? (
              schedulePreview.map((item) => (
                <article
                  key={item.entry_id}
                  className={styles.schedulePreviewCard}
                >
                  <div className={styles.schedulePreviewMeta}>
                    <span>{item.date_label || "Дата уточняется"}</span>
                    <span>{item.time_label || "Время уточняется"}</span>
                  </div>
                  {item.presenter_name ? (
                    <p className={styles.schedulePreviewSpeaker}>
                      {item.presenter_name}
                    </p>
                  ) : null}
                  <h3>{item.title}</h3>
                  <p className={styles.schedulePreviewTrack}>
                    {item.track_label || "ЮФУ.Про"}
                  </p>
                </article>
              ))
            ) : (
              <article className={styles.schedulePreviewEmpty}>
                <p>
                  Расписание уже готовится. Полная версия доступна на платформе.
                </p>
              </article>
            )}
          </div>
        </section>

        <section className={styles.tracksSection} id="tracks">
          <h2>
            Направления <span>Проектов</span>
          </h2>

          <div className={styles.tracksTopGrid}>
            {TRACKS_TOP.map((track, idx) => (
              <article
                key={track.title}
                className={`${styles.trackCard} ${styles[`track_${track.tone}`]} ${idx === 1 ? styles.trackCenter : ""}`}
              >
                <h3>{track.title}</h3>
                <p>{track.description}</p>
                <span>{track.note}</span>
              </article>
            ))}
          </div>

          {/* <div className={styles.tracksBottomGrid}>
            {TRACKS_BOTTOM.map((track) => (
              <article key={track.title} className={styles.trackWideCard}>
                <h3>{track.title}</h3>
                <p>{track.description}</p>
              </article>
            ))}
          </div> */}
        </section>

        <section className={styles.showcaseSection} id="cases">
          <div className={styles.sectionHeadBetween}>
            <h2>
              Кейсы <span>партнеров</span>
            </h2>
            <Link to="/cases" className={styles.btnOutlineLarge}>
              Смотреть всю витрину
            </Link>
          </div>

          <div className={styles.showcaseGrid}>
            {showcasePreview.length ? (
              showcasePreview.map((item) => (
                <article key={item.case_id} className={styles.showcaseCard}>
                  <p className={styles.showcasePartner}>{item.partner_name}</p>
                  <h3>{item.title}</h3>
                  <p className={styles.showcaseProblem}>{item.problem}</p>
                  <div className={styles.showcaseMeta}>
                    {/* {item.track ? <span>{item.track}</span> : null} */}
                    {item.location ? <span>{item.location}</span> : null}
                    {/* {item.rewards ? <span>С вознаграждением</span> : null} */}
                  </div>
                  <div className={styles.showcaseCardFooter}>
                    <Link
                      to={`/cases?focus=${item.case_id}`}
                      className={styles.showcaseMoreBtn}
                    >
                      Подробнее
                    </Link>
                  </div>
                </article>
              ))
            ) : (
              <article className={styles.showcaseEmpty}>
                <p>
                  Витрина наполняется. Полный список кейсов доступен на
                  отдельной странице.
                </p>
                <Link to="/cases" className={styles.btnPrimary}>
                  Перейти в витрину
                </Link>
              </article>
            )}
          </div>
        </section>

        <section className={styles.benefitsSection}>
          <div className={styles.benefitsGrid}>
            <div>
              <h2>
                Что получает
                <br />
                <span>участник</span>
              </h2>
              <ul>
                {BENEFITS.map((item, idx) => (
                  <li key={item}>
                    <b>{String(idx + 1).padStart(2, "0")}</b>
                    <p>{item}</p>
                  </li>
                ))}
              </ul>
            </div>
            <div className={styles.benefitsVisual} aria-hidden="true">
              {/* <div className={styles.benefitsGlow} /> */}
              {/* <div className={styles.benefitsPanel}> */}
              <img className={styles.benefitsPanel} src={team} alt="" />
              {/* </div> */}
            </div>
          </div>
        </section>

        <section className={styles.expertSection} id="experts">
          <div className={styles.expertCard}>
            <h2>
              Поделитесь своим <span>опытом</span>
            </h2>
            <p>
              Для экспертов из индустрии и академической среды открыт формат
              спокойного сотрудничества с командами: можно подключаться и давать
              содержательную обратную связь по своему профилю.
            </p>
            <a
              className={styles.btnPrimaryLarge}
              href={
                recruiting && season?.expert_application_url
                  ? season.expert_application_url
                  : withSeason("/seasons")
              }
              target="_blank"
              rel="noreferrer"
            >
              {recruiting && season?.expert_application_url
                ? "Стать экспертом"
                : "О сезонах"}
            </a>
          </div>
        </section>
      </main>

      <footer className={styles.footer}>
        <div className={styles.footerGrid}>
          <div>
            <p className={styles.footerBrand}>ЮФУ.Про</p>
            <p className={styles.footerText}>
              Экосистема проектной деятельности Южного федерального
              университета.
            </p>
          </div>

          <div>
            <p className={styles.footerTitle}>Навигация</p>
            <ul>
              {NAV_ITEMS.map((item) => (
                <li key={item.href}>
                  <a href={item.href}>{item.label}</a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className={styles.footerTitle}>Сообщество</p>
            <ul>
              {SOCIALS.map((item) => (
                <li key={item.label}>
                  <a href={item.href} target="_blank" rel="noreferrer">
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className={styles.footerTitle}>Контакты</p>
            <p className={styles.footerText}>sfedupro@yandex.com</p>
            <p className={styles.footerText}>
              По всем техническим доработкам и предложениям пишите на эту почту.
            </p>
            <p className={styles.footerText}>
              г. Ростов-на-Дону, Б. Садовая, 105
            </p>
          </div>
        </div>

        <p className={styles.footerCopy}>
          © 2026 ЮФУ.Про. Академическая экосистема проектов.
        </p>
      </footer>
    </div>
  );
}
